import { ApiError, type ApiErrorBody } from "../types/api";
import { getRuntimeConfig } from "../config/runtime";
import { getApiAuthorizationToken, getAuthorizationToken, signOutTo } from "../auth/cognito";
import { clearSelectedAccountId, getSelectedAccountId } from "../account/selection";

export type AccessTokenProvider = () => Promise<string | null | undefined> | string | null | undefined;

export type PortalContext = "admin" | "client";

export interface HttpClientOptions {
  baseUrl?: string;
  accessTokenProvider?: AccessTokenProvider;
  portal?: PortalContext;
}


const inFlightGetRequests = new Map<string, Promise<unknown>>();

function inFlightGetKey(url: string, headers: Headers): string {
  return [
    url,
    headers.get("Authorization") ?? "",
    headers.get("X-Surelinc-Portal") ?? "",
    headers.get("X-Surelinc-Account-Id") ?? "",
  ].join("\n");
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(baseUrl: string, path: string, query?: RequestOptions["query"]): string {
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${normalizedBase}${normalizedPath}`);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

export async function apiGet<TResponse>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<TResponse> {
  const client = createHttpClient();
  return client.get<TResponse>(path, options);
}

export async function apiPost<TResponse>(
  path: string,
  body?: unknown,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<TResponse> {
  const client = createHttpClient();
  return client.post<TResponse>(path, body, options);
}

export async function apiPatch<TResponse>(
  path: string,
  body?: unknown,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<TResponse> {
  const client = createHttpClient();
  return client.patch<TResponse>(path, body, options);
}

export async function apiDelete<TResponse>(
  path: string,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<TResponse> {
  const client = createHttpClient();
  return client.delete<TResponse>(path, options);
}

async function parseJsonSafely(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

async function resolveToken(provider?: AccessTokenProvider): Promise<string | undefined> {
  if (!provider) return undefined;
  const token = await provider();
  return token ? String(token) : undefined;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly accessTokenProvider: AccessTokenProvider;
  private readonly portal?: PortalContext;

  constructor(options: HttpClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? getRuntimeConfig().apiBaseUrl;
    this.accessTokenProvider = options.accessTokenProvider ?? getApiAuthorizationToken;
    this.portal = options.portal;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = await resolveToken(this.accessTokenProvider);
    const fallbackToken = token ?? getApiAuthorizationToken() ?? getAuthorizationToken() ?? undefined;
    const headers = new Headers(options.headers);

    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }

    let requestBody: BodyInit | undefined;
    if (options.body !== undefined) {
      headers.set("Content-Type", "application/json");
      requestBody = JSON.stringify(options.body);
    }

    if (fallbackToken) {
      // Match the older working deployment pattern: API calls use the Cognito
      // ID token and preserve the standard Bearer Authorization header format.
      const normalizedToken = fallbackToken.replace(/^Bearer\s+/i, "");
      headers.set("Authorization", `Bearer ${normalizedToken}`);
    }

    if (this.portal && !headers.has("X-Surelinc-Portal")) {
      headers.set("X-Surelinc-Portal", this.portal);
    }

    if (this.portal === "client" && path.replace(/\/+$/, "") !== "/client/accounts" && !headers.has("X-Surelinc-Account-Id")) {
      const accountId = getSelectedAccountId();
      if (accountId) {
        headers.set("X-Surelinc-Account-Id", accountId);
      }
    }

    const url = buildUrl(this.baseUrl, path, options.query);
    const method = String(options.method ?? "GET").toUpperCase();
    const execute = async () => {
      const response = await fetch(url, {
        ...options,
        body: requestBody,
        headers,
      });
      return { response, parsed: await parseJsonSafely(response) };
    };

    let resultPromise: Promise<{ response: Response; parsed: unknown }>;
    let getKey = "";
    if (method === "GET") {
      getKey = inFlightGetKey(url, headers);
      const existing = inFlightGetRequests.get(getKey) as Promise<{ response: Response; parsed: unknown }> | undefined;
      resultPromise = existing ?? execute();
      if (!existing) {
        inFlightGetRequests.set(getKey, resultPromise);
      }
    } else {
      resultPromise = execute();
    }

    let response: Response;
    let parsed: unknown;
    try {
      ({ response, parsed } = await resultPromise);
    } finally {
      if (getKey && inFlightGetRequests.get(getKey) === resultPromise) {
        inFlightGetRequests.delete(getKey);
      }
    }

    if (!response.ok) {
      const body = parsed as ApiErrorBody | undefined;
      const errorCode = typeof body?.errorCode === "string" ? body.errorCode : "";
      if (this.portal === "client" && response.status === 403 && errorCode === "CLIENT_DISABLED") {
        clearSelectedAccountId();
        signOutTo(`${window.location.origin}/account-disabled`);
      }
      if (this.portal === "client" && response.status === 409 && errorCode === "ACCOUNT_SELECTION_REQUIRED") {
        // Do not destroy a valid account selection because one endpoint failed
        // to consume the account-scoping header.  When an account is already
        // selected, surface the API error in place so the user stays within the
        // current AWS account boundary.  Redirect only when no selection exists.
        if (!getSelectedAccountId()) {
          window.location.assign("/select-account");
        }
      }
      const message = body?.message ?? body?.error ?? `API request failed with status ${response.status}`;
      throw new ApiError(message, response.status, body);
    }

    return parsed as T;
  }

  get<T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "POST", body });
  }

  put<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "PUT", body });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "PATCH", body });
  }

  delete<T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }
}

export function createHttpClient(options: HttpClientOptions = {}): HttpClient {
  return new HttpClient({
    ...options,
    accessTokenProvider: options.accessTokenProvider ?? getApiAuthorizationToken,
  });
}